// This file is automatically generated by webgen from the struct descriptions in /generated-json/structs.
// Files in /generated-json are created from the structs configured in the 'main' function of /cmd/struct2json/main.go.
//
// DO NOT EDIT THIS FILE except where designated below.

/* eslint-disable no-relative-import-paths/no-relative-import-paths */

import type { DateTime } from 'luxon';
import type { PartialDeep } from 'type-fest';

import * as DateHelpers from '../../../utils/dateHelpers';
import type { ExcludeMethodsDeep } from '../../../utils/types';
import { NIL_UUID } from '../../../utils/uuid';
import {
  type AdjustedPricingCompany,
  type AdjustedPricingCompanyWireFormat,
  NewAdjustedPricingCompany,
} from '../AdjustedPricingCompany/AdjustedPricingCompany';
import {
  type AdjustedPricingPlant,
  type AdjustedPricingPlantWireFormat,
  NewAdjustedPricingPlant,
} from '../AdjustedPricingPlant/AdjustedPricingPlant';
import Enums from '../Enums';
import {
  type MarketSummary,
  type MarketSummaryWireFormat,
  NewMarketSummary,
} from '../MarketSummary/MarketSummary';
import {
  NewUserSummary,
  type UserSummary,
  type UserSummaryWireFormat,
} from '../UserSummary/UserSummary';

export class AdjustedPricing {
  readonly id: string;
  readonly name: string;
  readonly description: string;
  readonly allCompanies: boolean;
  readonly companies: AdjustedPricingCompany[];
  readonly plants: AdjustedPricingPlant[];
  readonly markets: MarketSummary[];
  readonly adjustmentType: Enums.MarginConfiguration;
  readonly adjustmentValue: string;
  readonly createdBy: UserSummary | null;
  readonly isArchived: boolean;
  readonly createdAt: DateTime;
  readonly updatedAt: DateTime;

  constructor(data: { [key: string]: any } = {}) {
    this.id = data.id === undefined ? NIL_UUID : data.id;
    this.name = data.name === undefined ? '' : data.name;
    this.description = data.description === undefined ? '' : data.description;
    this.allCompanies = data.allCompanies === undefined ? false : data.allCompanies;
    this.companies = (data.companies ?? []).map((companies: any) =>
      NewAdjustedPricingCompany(companies),
    );
    this.plants = (data.plants ?? []).map((plants: any) => NewAdjustedPricingPlant(plants));
    this.markets = (data.markets ?? []).map((markets: any) => NewMarketSummary(markets));
    this.adjustmentType =
      data.adjustmentType === undefined
        ? Enums.MarginConfiguration.Percentage
        : data.adjustmentType;
    this.adjustmentValue = data.adjustmentValue === undefined ? '0' : data.adjustmentValue;
    this.createdBy =
      // eslint-disable-next-line no-nested-ternary
      (data.createdBy ?? null) === null ? null : NewUserSummary(data.createdBy);
    this.isArchived = data.isArchived === undefined ? false : data.isArchived;
    this.createdAt = DateHelpers.ParseServerDateTime(data.createdAt);
    this.updatedAt = DateHelpers.ParseServerDateTime(data.updatedAt);
  }

  static zero(): AdjustedPricing {
    const zeroValues: ExcludeMethodsDeep<AdjustedPricing> = {
      id: NIL_UUID,
      name: '',
      description: '',
      allCompanies: false,
      companies: [],
      plants: [],
      markets: [],
      adjustmentType: Enums.MarginConfiguration.Percentage,
      adjustmentValue: '0',
      createdBy: null,
      isArchived: false,
      createdAt: DateHelpers.ParseServerDateTime(DateHelpers.ZERO_DATE_STRING),
      updatedAt: DateHelpers.ParseServerDateTime(DateHelpers.ZERO_DATE_STRING),
    };
    return new AdjustedPricing(zeroValues);
  }

  // ************* DO NOT EDIT ABOVE THIS LINE *************
  // ************* DO NOT EDIT BELOW THIS LINE *************
}

export type AdjustedPricingWireFormat = {
  id: string;
  name: string;
  description: string;
  allCompanies: boolean;
  companies: AdjustedPricingCompanyWireFormat[];
  plants: AdjustedPricingPlantWireFormat[];
  markets: MarketSummaryWireFormat[];
  adjustmentType: string;
  adjustmentValue: string;
  createdBy: UserSummaryWireFormat | null;
  isArchived: boolean;
  createdAt: string;
  updatedAt: string;
};

type NewAdjustedPricingProps = PartialDeep<AdjustedPricing, { recurseIntoArrays: true }>;
export const NewAdjustedPricing: (props: NewAdjustedPricingProps) => AdjustedPricing = (props) =>
  new AdjustedPricing(props);

type NewAdjustedPricingFromWireFormatProps = PartialDeep<
  AdjustedPricingWireFormat,
  { recurseIntoArrays: true }
>;
export const NewAdjustedPricingFromWireFormat: (
  props: NewAdjustedPricingFromWireFormatProps,
) => AdjustedPricing = (props) => new AdjustedPricing(props);
